// Spec 078 FR-009 — "Associar a máquinas…": escolhe a linha, marca as máquinas, salva. Cada
// associação é o PATCH da máquina (uma mutação por máquina, um toast só no fim).
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ModalOverlay } from "@/components/ui/modal-overlay";
import { Notify, toast } from "@/components/ui/notify";
import { useLineMachines, useLines } from "@/features/lines/hooks";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { useAssignModel3d } from "../hooks";
import type { Model3dSummary } from "@/types/models-3d.types";

export interface AssignMachinesDialogProps {
  model: Model3dSummary;
  onClose: () => void;
}

export function AssignMachinesDialog({
  model,
  onClose,
}: AssignMachinesDialogProps): React.ReactElement {
  const lines = useLines();
  const [lineId, setLineId] = React.useState<string>("");
  const machines = useLineMachines(lineId);
  const assign = useAssignModel3d();
  const [checked, setChecked] = React.useState<Set<string>>(new Set());

  // A seleção é do USUÁRIO; a query só a semeia. Ressincronizar a cada `machines.data` novo fazia
  // o próprio `save()` apagar a escolha: a primeira máquina salva invalida
  // ["lines", lineId, "machines"], o refetch chega no meio do laço e desmarca as pendentes — se
  // então uma falhar, o diálogo fica aberto com a intenção do usuário perdida e sem aviso.
  const seeded = React.useRef<string | null>(null);
  React.useEffect(() => {
    const key = `${model.id}:${lineId}`;
    if (!machines.data || seeded.current === key) return;
    seeded.current = key;
    setChecked(new Set(machines.data.filter((m) => m.model3dId === model.id).map((m) => m.id)));
  }, [machines.data, model.id, lineId]);

  function toggle(id: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function save() {
    const current = machines.data ?? [];
    const changes = current.filter((m) => (m.model3dId === model.id) !== checked.has(m.id));
    try {
      for (const m of changes) {
        await assign.mutateAsync({
          lineId,
          machineId: m.id,
          model3dId: checked.has(m.id) ? model.id : null,
        });
      }
      toast.success(
        changes.length === 0 ? "Nada a alterar." : `${changes.length} máquina(s) atualizada(s).`
      );
      onClose();
    } catch (err) {
      toast.error("Falha ao associar.", { description: getFriendlyErrorMessage(err) });
    }
  }

  return (
    <ModalOverlay
      onClose={onClose}
      title={`Associar ${model.name} a máquinas`}
      className="max-w-lg"
    >
      <div className="flex flex-col gap-4 p-5">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">
            Associar “{model.name}” a máquinas
          </h2>
          <p className="text-sm text-text-secondary">
            Todas as máquinas marcadas passam a usar este modelo e a mesma calibração.
          </p>
        </div>
        {lines.isError ? (
          <Notify.QueryError
            error={lines.error}
            variant="inline"
            onRetry={() => void lines.refetch()}
          />
        ) : null}
        <div className="space-y-1.5">
          <Label htmlFor="m3d-assign-line">Linha</Label>
          <select
            id="m3d-assign-line"
            value={lineId}
            onChange={(e) => setLineId(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
          >
            <option value="">Escolha a linha…</option>
            {(lines.data ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        {lineId ? (
          machines.isError ? (
            <Notify.QueryError
              error={machines.error}
              variant="inline"
              onRetry={() => void machines.refetch()}
            />
          ) : (
            <ul
              className="max-h-64 divide-y divide-border-subtle overflow-auto rounded-md border"
              aria-label="Máquinas da linha"
            >
              {(machines.data ?? []).map((m) => (
                <li key={m.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <input
                    id={`m3d-assign-${m.id}`}
                    type="checkbox"
                    checked={checked.has(m.id)}
                    onChange={() => toggle(m.id)}
                    className="h-4 w-4"
                  />
                  <label htmlFor={`m3d-assign-${m.id}`} className="flex-1 cursor-pointer">
                    <span className="font-medium text-text-primary">{m.name}</span>
                    <span className="ml-2 font-mono text-xs text-text-tertiary">{m.code}</span>
                    {m.model3dId && m.model3dId !== model.id ? (
                      <span className="ml-2 text-xs text-text-tertiary">(usa outro modelo)</span>
                    ) : null}
                  </label>
                </li>
              ))}
              {machines.data?.length === 0 ? (
                <li className="px-3 py-2 text-sm text-text-tertiary">
                  Esta linha não tem máquinas.
                </li>
              ) : null}
            </ul>
          )
        ) : null}
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="ghost" onClick={onClose} disabled={assign.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => void save()} disabled={!lineId || assign.isPending}>
            {assign.isPending ? "Salvando…" : "Salvar"}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}
